import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PixelButton from "@/components/PixelButton";
import AshLogo from "@/components/AshLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  const token = typeof window !== "undefined" ? localStorage.getItem("mt_admin_token") : null;

  useEffect(() => {
    if (!token) {
      nav("/admin");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/leads`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLeads(data);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem("mt_admin_token");
          nav("/admin");
        } else {
          setError("Failed to load leads");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [nav, token]);

  const filtered = leads.filter((l) => {
    const q = query.toLowerCase();
    return (
      !q ||
      (l.name || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.business_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen px-3 md:px-6 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <AshLogo size={40} />
            <div>
              <div className="pixel-font text-sm text-[#F58634]">MARKETING TRAIL — LEADS</div>
              <div className="ash-byline text-base">
                <span className="ash">ASH</span><span className="interactive">interactive</span>
                <span className="vt323 text-base text-white/60 ml-2">· {leads.length} total submissions</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              data-testid="admin-export-csv"
              className="pixel-btn pixel-btn-dark"
              href={`${API}/admin/leads.csv?token=${token || ""}`}
              target="_blank"
              rel="noreferrer"
            >
              ⬇ Export CSV
            </a>
            <PixelButton
              variant="secondary"
              data-testid="admin-logout"
              onClick={() => {
                localStorage.removeItem("mt_admin_token");
                nav("/admin");
              }}
            >
              Logout
            </PixelButton>
          </div>
        </div>

        <input
          data-testid="admin-search"
          className="pixel-input mb-4"
          placeholder="Search by name, email or business…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {loading ? (
          <div className="pixel-panel">Loading leads…</div>
        ) : error ? (
          <div className="pixel-panel text-red-700">{error}</div>
        ) : (
          <div className="pixel-panel overflow-x-auto" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b-4 border-black">
                  {["Date", "Name", "Email", "Business", "Goal", "Score", "Title", "Contact?"].map((h) => (
                    <th key={h} className="pixel-font text-[10px] text-[#F26522] py-2 pr-3 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const g = l.game_result || {};
                  return (
                    <tr key={l.id} className="border-b border-black/20">
                      <td className="py-2 pr-3 vt323 text-base whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3">{l.name}</td>
                      <td className="py-2 pr-3 text-[#F26522]">{l.email}</td>
                      <td className="py-2 pr-3">{l.business_name}</td>
                      <td className="py-2 pr-3 text-xs">{g.main_goal}</td>
                      <td className="py-2 pr-3 font-bold">{g.final_score}</td>
                      <td className="py-2 pr-3 text-xs">{g.final_title}</td>
                      <td className="py-2 pr-3">
                        {l.contact_consent ? (
                          <span className="pixel-font text-[9px] bg-emerald-200 border-2 border-black px-1.5 py-0.5">
                            YES
                          </span>
                        ) : (
                          <span className="pixel-font text-[9px] bg-gray-200 border-2 border-black px-1.5 py-0.5">
                            NO
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-[#1A1A1A]/60">
                      No leads found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
